import { apiClient } from '../client';
import type { components } from '../schema.gen';

// ── Types from generated schema ──────────────────────────────────────────────
export type VenueConnectionRead = components['schemas']['VenueConnectionRead'];
export type VenueConnectionListResponse = components['schemas']['VenueConnectionListResponse'];
export type VenueConnectionCreate = components['schemas']['VenueConnectionCreate'];
export type VenueConnectionUpdate = components['schemas']['VenueConnectionUpdate'];
export type ConnectivityTestResponse = components['schemas']['ConnectivityTestResponse'];
export type VenueBalanceResponse = components['schemas']['VenueBalanceResponse'];
export type VenueSymbolsResponse = components['schemas']['VenueSymbolsResponse'];
export type VenueConnectionConfigResponse = components['schemas']['VenueConnectionConfigResponse'];
export type LiveStatusResponse = components['schemas']['LiveStatusResponse'];
export type LiveHistoryResponse = components['schemas']['LiveHistoryResponse'];
export type ConfirmLiveRequest = components['schemas']['ConfirmLiveRequest'];
export type ConfirmLiveResponse = components['schemas']['ConfirmLiveResponse'];
export type RevokeLiveRequest = components['schemas']['RevokeLiveRequest'];
export type RevokeLiveResponse = components['schemas']['RevokeLiveResponse'];
// Types not yet in schema.gen.ts — defined inline
export type RotateInitiateResponse = {
  rotation_id: string;
  venue_connection_id: string;
  status: string;
  initiated_at: string;
  expires_at: string;
};

export type RotateStatusResponse = {
  rotation_id: string;
  status: string;
  initiated_at: string;
  completed_at: string | null;
};

export type RotateCompleteRequest = {
  api_key: string;
  api_secret: string;
  passphrase?: string | null;
};

export type RotateCompleteResponse = {
  rotation_id: string;
  status: string;
  completed_at: string;
};

export interface VenueConnectionListParams {
  limit?: number;
  offset?: number;
  platform?: string;
  /** Include system-provisioned paper trading connections (default: true in frontend) */
  include_system?: boolean;
  [key: string]: unknown;
}

// ── CRUD ─────────────────────────────────────────────────────────────────────

export async function getVenueConnections(
  params: VenueConnectionListParams = {},
): Promise<VenueConnectionListResponse> {
  const { data } = await apiClient.get<VenueConnectionListResponse>('/api/v1/venue-connections', {
    params,
  });
  return data;
}

export async function getVenueConnection(id: string): Promise<VenueConnectionRead> {
  const { data } = await apiClient.get<VenueConnectionRead>(`/api/v1/venue-connections/${id}`);
  return data;
}

export async function createVenueConnection(
  body: VenueConnectionCreate,
): Promise<VenueConnectionRead> {
  const { data } = await apiClient.post<VenueConnectionRead>('/api/v1/venue-connections', body);
  return data;
}

export async function updateVenueConnection(
  id: string,
  body: VenueConnectionUpdate,
): Promise<VenueConnectionRead> {
  const { data } = await apiClient.patch<VenueConnectionRead>(
    `/api/v1/venue-connections/${id}`,
    body,
  );
  return data;
}

export async function deleteVenueConnection(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/venue-connections/${id}`);
}

// ── Connectivity & Balance ───────────────────────────────────────────────────

export async function testVenueConnectivity(id: string): Promise<ConnectivityTestResponse> {
  const { data } = await apiClient.post<ConnectivityTestResponse>(
    `/api/v1/venue-connections/${id}/test`,
  );
  return data;
}

export async function getVenueBalance(id: string): Promise<VenueBalanceResponse> {
  const { data } = await apiClient.get<VenueBalanceResponse>(
    `/api/v1/venue-connections/${id}/balance`,
  );
  return data;
}

export async function getVenueSymbols(id: string): Promise<VenueSymbolsResponse> {
  const { data } = await apiClient.get<VenueSymbolsResponse>(
    `/api/v1/venue-connections/${id}/symbols`,
  );
  return data;
}

export async function getVenueConnectionConfig(id: string): Promise<VenueConnectionConfigResponse> {
  const { data } = await apiClient.get<VenueConnectionConfigResponse>(
    `/api/v1/venue-connections/${id}/config`,
  );
  return data;
}

// ── LIVE Ceremony ────────────────────────────────────────────────────────────

export async function getVenueLiveStatus(id: string): Promise<LiveStatusResponse> {
  const { data } = await apiClient.get<LiveStatusResponse>(
    `/api/v1/venue-connections/${id}/live-status`,
  );
  return data;
}

export async function getVenueLiveHistory(
  id: string,
  params: { limit?: number; offset?: number } = {},
): Promise<LiveHistoryResponse> {
  const { data } = await apiClient.get<LiveHistoryResponse>(
    `/api/v1/venue-connections/${id}/live-history`,
    { params },
  );
  return data;
}

export async function confirmVenueLive(
  id: string,
  body: ConfirmLiveRequest,
  mfaCode?: string,
): Promise<ConfirmLiveResponse> {
  const { data } = await apiClient.post<ConfirmLiveResponse>(
    `/api/v1/venue-connections/${id}/confirm-live`,
    body,
    mfaCode ? { headers: { 'X-MFA-Code': mfaCode } } : undefined,
  );
  return data;
}

export async function revokeVenueLive(
  id: string,
  body: RevokeLiveRequest,
): Promise<RevokeLiveResponse> {
  const { data } = await apiClient.post<RevokeLiveResponse>(
    `/api/v1/venue-connections/${id}/revoke-live`,
    body,
  );
  return data;
}

// ── Key Rotation (4-step flow) ───────────────────────────────────────────────

export async function initiateKeyRotation(id: string): Promise<RotateInitiateResponse> {
  const { data } = await apiClient.post<RotateInitiateResponse>(
    `/api/v1/venue-connections/${id}/rotate`,
  );
  return data;
}

export async function getKeyRotationStatus(id: string): Promise<RotateStatusResponse> {
  const { data } = await apiClient.get<RotateStatusResponse>(
    `/api/v1/venue-connections/${id}/rotate/status`,
  );
  return data;
}

export async function completeKeyRotation(
  id: string,
  body: RotateCompleteRequest,
): Promise<RotateCompleteResponse> {
  const { data } = await apiClient.post<RotateCompleteResponse>(
    `/api/v1/venue-connections/${id}/rotate/complete`,
    body,
  );
  return data;
}

export async function abortKeyRotation(id: string): Promise<void> {
  await apiClient.post(`/api/v1/venue-connections/${id}/rotate/abort`);
}

// ── Wizard endpoints (M1/M2) ────────────────────────────────────────────────
// Types not yet in schema.gen.ts — defined inline until openapi.json is regenerated

export type QuickSetupRequest = {
  platform: string;
  api_key: string;
  api_secret: string;
  passphrase?: string | null;
  label?: string | null;
};

export type BalanceSummary = {
  asset: string;
  // Backend AssetBalance serializes the free/available balance as `available`.
  available: string;
  locked: string;
  total: string;
};

type WalletSummaryEntry = {
  wallet_id: string;
  environment: string;
  is_stale: boolean;
  last_synced_at: string | null;
  balances: BalanceSummary[];
  total_equity_usd: string | null;
};

type RawWalletSummary = {
  paper?: WalletSummaryEntry | null;
  live?: WalletSummaryEntry | null;
};

export type ConnectionWithBalances = VenueConnectionRead & {
  balances: BalanceSummary[] | null;
  last_balance_sync_at: string | null;
  wallet_summary?: RawWalletSummary | null;
};

export type ConnectionWithBalancesListResponse = {
  items: ConnectionWithBalances[];
  total: number;
};

export type SyncBalanceResponse = {
  connection_id: string;
  balances: BalanceSummary[];
  synced_at: string;
};

// Mirrors backend AllocationCapacityResponse (LOK-1044). schema.gen.ts does not
// generate this type (manual bridge) — keep field names in sync with the route.
export type AllocationCapacityResponse = {
  venue_connection_id: string;
  wallet_id: string | null;
  balances: Array<{ asset: string; available: string; locked: string; total: string }>;
  budget: { cap: string | null; allocated: string; headroom: string | null };
  bot_allocations: Array<{
    bot_id: string;
    bot_name: string;
    allocated_capital: string;
    used_capital: string;
  }>;
  total_allocated: string;
  // Headroom for new allocations; null when the connection is uncapped.
  available_for_allocation: string | null;
};

export async function quickSetupConnection(body: QuickSetupRequest): Promise<VenueConnectionRead> {
  const { data } = await apiClient.post<VenueConnectionRead>(
    '/api/v1/venue-connections/quick-setup',
    body,
  );
  return data;
}

export async function getVenueConnectionsWithBalances(
  params: VenueConnectionListParams = {},
): Promise<ConnectionWithBalancesListResponse> {
  const { data } = await apiClient.get<ConnectionWithBalancesListResponse>(
    '/api/v1/venue-connections',
    { params: { ...params, include_balances: true } },
  );
  // Backend returns wallet_summary.live/paper — map to flat balances for ConnectionCard
  const items = data.items.map((conn) => {
    const live = conn.wallet_summary?.live;
    const paper = conn.wallet_summary?.paper;
    const activeWallet = live ?? paper ?? null;
    return {
      ...conn,
      balances: activeWallet?.balances ?? null,
      last_balance_sync_at: activeWallet?.last_synced_at ?? null,
    };
  });
  return { ...data, items };
}

export async function syncVenueBalance(id: string): Promise<SyncBalanceResponse> {
  const { data } = await apiClient.post<SyncBalanceResponse>(
    `/api/v1/venue-connections/${id}/sync-balance`,
  );
  return data;
}

export async function getAllocationCapacity(id: string): Promise<AllocationCapacityResponse> {
  const { data } = await apiClient.get<AllocationCapacityResponse>(
    `/api/v1/venue-connections/${id}/allocation-capacity`,
  );
  return data;
}
